import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: 'sync'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API}/contact/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Message Sent",
          description: t('contact.success'),
        });
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          type: 'sync'
        });
      } else {
        toast({
          title: "Error",
          description: t('contact.error'),
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: t('contact.error'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-7xl font-light tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-white mb-8">
            {t('contact.title')}
          </h2>
          <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-1 gap-12 max-w-2xl mx-auto">
          <Card className="bg-black/40 border-white/10 backdrop-blur-sm p-8">
            <h3 className="text-2xl font-light text-white mb-6">{t('contact.sendMessage')}</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  type="text"
                  name="name"
                  placeholder={t('contact.name')}
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-black/40 border-white/20 text-white placeholder:text-gray-400 focus:border-blue-400 focus:ring-blue-400/20 rounded-none"
                  required
                  disabled={isSubmitting}
                />
                <Input
                  type="email"
                  name="email"
                  placeholder={t('contact.email')}
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-black/40 border-white/20 text-white placeholder:text-gray-400 focus:border-blue-400 focus:ring-blue-400/20 rounded-none"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-gray-400 text-sm font-light">{t('contact.inquiryType')}</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="w-full bg-black/40 border border-white/20 text-white focus:border-blue-400 focus:ring-blue-400/20 rounded-none p-3"
                >
                  <option value="sync">{t('contact.types.sync')}</option>
                  <option value="collaboration">{t('contact.types.collaboration')}</option>
                  <option value="general">{t('contact.types.general')}</option>
                </select>
              </div>
              
              <Input
                type="text"
                name="subject"
                placeholder={t('contact.subject')}
                value={formData.subject}
                onChange={handleChange}
                disabled={isSubmitting}
                className="bg-black/40 border-white/20 text-white placeholder:text-gray-400 focus:border-blue-400 focus:ring-blue-400/20 rounded-none"
              />
              
              <Textarea
                name="message"
                placeholder={t('contact.message')}
                value={formData.message}
                onChange={handleChange}
                rows={6}
                disabled={isSubmitting}
                className="bg-black/40 border-white/20 text-white placeholder:text-gray-400 focus:border-blue-400 focus:ring-blue-400/20 rounded-none resize-none"
                required
              />
              
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-transparent border border-white/30 text-white hover:bg-white hover:text-black transition-all duration-300 h-12 font-light tracking-widest rounded-none backdrop-blur-sm disabled:opacity-50"
              >
                {isSubmitting ? '...' : t('contact.send')}
              </Button>
            </form>
          </Card>
        </div>

        <div className="text-center mt-16 pt-8 border-t border-white/10">
          <p className="text-gray-400 font-light">
            {t('contact.copyright')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;